import { isApiError } from '@/api/http/errors';

/**
 * 첫 입주일 변경·미드텀 전환 요청에 대한 서버의 409를 코드로 구분한다(BE 실제 계약).
 * - FIRST_DATE_BEFORE_CONTRACT_END(첫날 PATCH만): 날짜가 계약 준비 기간 전이다 → 하한을 다시 조회해 그 날짜 이후로 고른다.
 * - REVISION_OR_COMMAND_CONFLICT: 판매 revision·명령이 맞지 않는다(전환 POST는 준비 전 거절도 이 코드) → 최신 preview/현재 판매를 다시 조회한다.
 * 그 밖의 코드나 코드가 없는 409는 어느 쪽인지 단정하지 않는다. 메시지 문자열은 읽지 않는다.
 */
export type FirstDateRefusal = 'before-contract-end' | 'conflict' | 'unclassified' | null;

export function firstDateRefusal(error: unknown): FirstDateRefusal {
  if (!isApiError(error) || error.kind !== 'http' || error.status !== 409) return null;
  if (error.code === 'FIRST_DATE_BEFORE_CONTRACT_END') return 'before-contract-end';
  if (error.code === 'REVISION_OR_COMMAND_CONFLICT') return 'conflict';
  return 'unclassified';
}

/**
 * 화면이 실제로 한 일에 맞춰 안내를 고른다.
 * - reread: 같은 거절 직후 읽기(GET)가 성공해 화면 값을 최신으로 맞췄다.
 * - not-reread: 다시 읽지 못했거나 읽지 않았다 → 직접 다시 조회하도록 안내한다.
 * - unverified: 재조회를 시도하지만 성공 여부를 알 수 없다 → 읽었다고 말하지 않고 화면 값을 확인하게 한다.
 * - held: 원 요청이 보존되어 새 검토가 막혀 있다 → 보존 요청 결과 확인만 가능하다.
 */
export type RefusalContext = 'reread' | 'not-reread' | 'unverified' | 'held';

/** 첫날 PATCH 거절 후 회복 안내 */
export function dateRefusalText(kind: Exclude<FirstDateRefusal, null>, context: RefusalContext) {
  if (context === 'held')
    return '서버가 보존된 원래 날짜 요청을 거절했습니다. 새 변경은 중단된 상태이니 원래 날짜 요청 결과를 확인하세요. 자동으로 다시 보내지 않습니다.';
  if (kind === 'before-contract-end')
    return context === 'reread'
      ? '선택한 날짜가 계약 준비 기간 전이라 서버가 거절했습니다. 가장 이른 날짜를 다시 조회했으니, 안내된 날짜 이후로 선택해 다시 검토하세요.'
      : context === 'unverified'
        ? '선택한 날짜가 계약 준비 기간 전이라 서버가 거절했습니다. 화면의 가장 이른 날짜 안내를 확인해 그 이후로 선택하세요. 안내가 없으면 현재 값을 다시 조회하세요.'
        : '선택한 날짜가 계약 준비 기간 전이라 서버가 거절했습니다. 가장 이른 날짜를 다시 조회하지 못했습니다. 현재 값을 다시 조회한 뒤 그 날짜 이후로 선택하세요.';
  if (kind === 'conflict')
    return context === 'reread'
      ? '다른 변경이 먼저 반영되어 판매 상태가 맞지 않아 서버가 거절했습니다. 현재 판매 현황과 가장 이른 날짜를 다시 조회했으니 확인한 뒤 다시 검토하세요.'
      : context === 'unverified'
        ? '다른 변경이 먼저 반영되어 판매 상태가 맞지 않아 서버가 거절했습니다. 화면의 현재 판매 현황을 확인하고, 다르면 다시 조회한 뒤 검토하세요.'
        : '다른 변경이 먼저 반영되어 판매 상태가 맞지 않아 서버가 거절했습니다. 최신 값을 다시 조회하지 못했습니다. 현재 값을 다시 조회한 뒤 검토하세요.';
  return '서버가 변경을 거절했습니다. 계약 준비 기간 전의 날짜이거나 판매 상태가 먼저 바뀌었을 수 있습니다. 현재 값을 다시 조회하세요.';
}

/**
 * 미드텀 전환 POST 거절 후 회복 안내 — 준비 전 거절과 revision 충돌이 같은 코드라 둘 다 설명한다.
 * scope: 화면이 객실인지 슬롯인지. held: 원 요청이 보존되어 새 검토가 막혀 있다(Room 전환).
 */
export function transitionRefusalText(
  kind: Exclude<FirstDateRefusal, null>,
  options: { scope: 'room' | 'unit'; context: RefusalContext },
) {
  const target = options.scope === 'unit' ? '슬롯' : '객실';
  if (options.context === 'held' && kind === 'unclassified')
    return `서버가 ${target} 현재 상태와 맞지 않는다며 거절했습니다. 원래 요청은 보존되어 있고 자동으로 다시 보내지 않으니, 판매 요청 상태 조회로 결과를 확인하세요.`;
  if (options.context === 'held')
    return `서버가 요청을 거절했습니다. 날짜가 계약 준비 기간 전이거나 판매 상태가 먼저 바뀌었을 수 있습니다. 원래 요청은 보존되어 있고 자동으로 다시 보내지 않으니, 판매 요청 상태 조회로 결과를 확인하세요. 같은 요청을 다시 시도하면 같은 거절을 받을 수 있습니다.`;
  if (kind === 'unclassified')
    return `서버가 ${target} 현재 상태와 맞지 않는다며 거절했습니다. 다시 조회해 확인하세요.`;
  return options.context === 'reread'
    ? '서버가 요청을 거절했습니다. 날짜가 계약 준비 기간 전이거나 판매 상태가 먼저 바뀌었을 수 있어, 최신 계약·판매 정보를 다시 조회했습니다. 확인한 뒤 다시 검토하세요.'
    : '서버가 요청을 거절했습니다. 날짜가 계약 준비 기간 전이거나 판매 상태가 먼저 바뀌었을 수 있습니다. 최신 정보를 다시 조회하지 못했으니 직접 다시 조회한 뒤 검토하세요.';
}

/**
 * 전환 POST의 4xx 중 서버가 요청을 받지 않았다고 볼 수 있는 거절인가.
 * 409 REVISION_OR_COMMAND_CONFLICT는 제외한다 — 실제 BE는 이미 명령을 남긴 뒤의 미해결 상태
 * (RoomSaleTransitionUnresolved)도 같은 코드로 돌려주므로(관측: 409 응답 뒤 pending·APPLIED), 409만으로는 미접수를 단정할 수 없다.
 */
export function transitionNotAccepted(error: unknown): boolean {
  return (
    isApiError(error) &&
    error.kind === 'http' &&
    error.status !== undefined &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 409
  );
}

/**
 * 원명령 상태 조회 404. receipt 부재 외에 다른 대상(Room·Unit 등)에서도 나고, 동시에 처리 중인 요청의 commit 전 조회와
 * 경합할 수도 있어 미실행의 증거가 아니다 — 보존을 풀거나 새 UUID를 만들지 않는다(백엔드 확인).
 */
export const transitionCommandMissing = (error: unknown): boolean =>
  isApiError(error) && error.kind === 'http' && error.status === 404;

/**
 * 상태 조회 404 뒤 안내. reread=false면 읽기 재조회가 실패한 것이다.
 * trace: 재조회한 preview가 원 요청의 expected revision 그대로이고 미해결이 없으면 'none'(반영 흔적 없음),
 * revision이 바뀌었거나 미해결 요청이 있으면 'changed'(원 요청 결과일 수 있음).
 */
export function missingStatusText(
  state: { reread: false } | { reread: true; trace: 'none' | 'changed' },
  /** 화면에 "같은 요청 다시 시도"가 실제로 있고 쓸 수 있는가(다른 로그인 세대·권한 없음이면 false) */
  canRetry = true,
) {
  const base =
    '상태 조회가 원래 요청을 찾지 못했지만, 이것만으로 접수되지 않았다고 확정할 수 없습니다(조회 대상 불일치나 처리 중인 요청과의 시점 차이일 수 있습니다). 원래 요청과 요청 번호는 그대로 보존했고 자동으로 다시 보내지 않으며 새 요청 번호도 만들지 않습니다.';
  if (!state.reread)
    return `${base} 권한·대상·최신 판매 정보를 다시 조회하지 못했습니다. 잠시 뒤 판매 요청 상태를 다시 조회하세요.`;
  return state.trace === 'none'
    ? `${base} 권한·대상·최신 계약·판매 정보를 다시 조회했고 아직 반영된 흔적은 보이지 않지만, 서버가 처리 중일 수 있습니다. ${
        canRetry
          ? '잠시 뒤 상태를 다시 조회하거나, 같은 요청을 다시 시도해 서버가 원래 요청으로 처리하게 하세요.'
          : '잠시 뒤 판매 요청 상태를 다시 조회하세요. 이 화면에서는 같은 요청을 다시 보낼 수 없습니다.'
      }`
    : `${base} 권한·대상·최신 계약·판매 정보를 다시 조회했더니 판매 상태가 바뀌었거나 미해결 요청이 있습니다. 원래 요청의 결과일 수 있으니 잠시 뒤 판매 요청 상태를 다시 조회하세요.`;
}
